import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { instance } from '@app/api/axios';
import { showToaster } from '@shared/ui/messages/useToaster';
import i18n from '@app/i18n';

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ count, start }, { rejectWithValue, dispatch }) => {
    if (start === 1) {
      dispatch(resetNotificationsNew());
    }
    try {
      const res = await instance.get('/notifications/unread', {
        params: { results: count, start },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const fetchOldNotifications = createAsyncThunk(
  'notifications/fetchOldNotifications',
  async ({ count, start }, { rejectWithValue, dispatch }) => {
    if (start === 1) {
      dispatch(resetNotificationsOld());
    }
    try {
      const res = await instance.get('/notifications', {
        params: { results: count, start },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const fetchNotificationsCount = createAsyncThunk(
  'notifications/fetchNotificationsCount',
  async (_, { rejectWithValue }) => {
    try {
      const res = await instance.get('/notifications/unread-count');
      return res.data?.count;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const markAllAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const res = await instance.post('/notifications/read-all');
      dispatch(resetNotificationsNew());
      dispatch(resetCountUnread());
      return res.data;
    } catch (err) {
      showToaster(
        'error',
        err?.response?.data?.message || i18n.t('request_error'),
      );
      return rejectWithValue(err.message);
    }
  },
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: {
    notifications_new: [],
    notifications_old: [],
    count_new: 0,
    count_old: 0,
    count_unread: 0,
    isLoading: false,
    isListLoading: true,
    error: null,
  },
  reducers: {
    resetNotificationsNew: (state) => {
      state.notifications_new = [];
      state.count_new = 0;
    },
    resetNotificationsOld: (state) => {
      state.notifications_old = [];
      state.count_old = 0;
    },
    resetCountUnread: (state) => {
      state.count_unread = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchNotifications.fulfilled, (state, action) => {
      state.isListLoading = false;
      state.error = null;
      state.count_new = action.payload?.total ?? 0;
      state.notifications_new = [
        ...state.notifications_new,
        ...(action.payload?.items || []),
      ];
    });
    builder.addCase(fetchNotificationsCount.fulfilled, (state, action) => {
      state.count_unread = action.payload ?? 0;
    });
    builder.addCase(fetchOldNotifications.fulfilled, (state, action) => {
      state.isListLoading = false;
      state.error = null;
      state.notifications_old = [
        ...state.notifications_old,
        ...(action.payload?.items || []),
      ];
      state.count_old = action.payload?.total ?? 0;
    });
    builder.addCase(markAllAsRead.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(markAllAsRead.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(markAllAsRead.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addMatcher(
      isAnyOf(fetchNotifications.pending, fetchOldNotifications.pending),
      (state) => {
        state.isListLoading = true;
      },
    );
    builder.addMatcher(
      isAnyOf(fetchNotifications.rejected, fetchOldNotifications.rejected),
      (state) => {
        state.isListLoading = false;
        state.error = true;
      },
    );
  },
});

export const {
  resetNotificationsNew,
  resetNotificationsOld,
  resetCountUnread,
} = notificationsSlice.actions;
export default notificationsSlice.reducer;
